import type { CreateEventConnectionRequest } from '@emma/api_client/types';
import {
  Button,
  Dropdown,
  FormControl,
  HorizontalStack,
  Input,
  Text,
  VerticalStack,
} from '@lego/klik-ui';
import { type ChangeEvent, type FormEvent, useEffect, useRef, useState } from 'react';
import {
  type SubmitStatus,
  SubmitStatusIndicator,
} from '@/components/common/SubmitStatusIndicator';
import { useAddWorkspaceEventConnection } from '@/hooks/workspace';

type Props = {
  workspaceId: number;
  onSuccess: () => void;
  onCancel: () => void;
};

type ConnectionType = 'MSTeams' | 'Pulsar';

const SUCCESS_DELAY_MS = 1500;

const DEFAULT_CONNECTION_TYPE: string[] = ['MSTeams'];

const buildBody = (
  type: ConnectionType,
  url: string,
  apiName: string,
  ammaWorkspaceName: string,
  topic: string
): CreateEventConnectionRequest['connectionBody'] => {
  switch (type) {
    case 'MSTeams':
      return { connectionType: 'MSTeams', url: url.trim() };
    case 'Pulsar':
      return {
        connectionType: 'Pulsar',
        apiName: apiName.trim(),
        ammaWorkspaceName: ammaWorkspaceName.trim(),
        topic: topic.trim(),
      };
  }
};

const validate = (
  type: ConnectionType,
  url: string,
  apiName: string,
  ammaWorkspaceName: string,
  topic: string
): string | null => {
  switch (type) {
    case 'MSTeams': {
      if (!url.trim()) return 'Webhook URL is required';
      try {
        const parsed = new URL(url.trim());
        if (parsed.protocol !== 'https:') return 'Webhook URL must use https://';
      } catch {
        return 'Webhook URL is not a valid URL';
      }
      return null;
    }
    case 'Pulsar': {
      if (!apiName.trim()) return 'API name is required';
      if (!ammaWorkspaceName.trim()) return 'AMMA workspace name is required';
      if (!topic.trim()) return 'Topic is required';
      return null;
    }
  }
};

export const AddEventConnectionForm = ({ workspaceId, onSuccess, onCancel }: Props) => {
  const [type, setType] = useState<ConnectionType>('MSTeams');
  const [url, setUrl] = useState('');
  const [apiName, setApiName] = useState('');
  const [ammaWorkspaceName, setAmmaWorkspaceName] = useState('');
  const [topic, setTopic] = useState('');
  const [validationError, setValidationError] = useState<string | null>(null);
  const [submitStatus, setSubmitStatus] = useState<SubmitStatus>('idle');
  const [submitError, setSubmitError] = useState<string | null>(null);

  const addConnection = useAddWorkspaceEventConnection(workspaceId);
  const successTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    return () => {
      if (successTimeoutRef.current) clearTimeout(successTimeoutRef.current);
    };
  }, []);

  const handleSubmit = async (e?: FormEvent) => {
    e?.preventDefault();
    setValidationError(null);
    setSubmitError(null);

    const error = validate(type, url, apiName, ammaWorkspaceName, topic);
    if (error !== null) {
      setValidationError(error);
      return;
    }

    setSubmitStatus('submitting');

    try {
      await addConnection({
        connectionBody: buildBody(type, url, apiName, ammaWorkspaceName, topic),
      });
      setSubmitStatus('success');
      successTimeoutRef.current = setTimeout(onSuccess, SUCCESS_DELAY_MS);
    } catch {
      setSubmitStatus('error');
      setSubmitError('Failed to create event connection');
    }
  };

  const isBusy = submitStatus === 'submitting' || submitStatus === 'success';

  return (
    <form onSubmit={handleSubmit}>
      <VerticalStack align="stretch" spacing={4}>
        <FormControl>
          <FormControl.Label>Connection Type</FormControl.Label>
          <Dropdown
            defaultValue={DEFAULT_CONNECTION_TYPE}
            isDisabled={isBusy}
            onChange={(val: string) => {
              setType(val as ConnectionType);
              setValidationError(null);
            }}
            shouldDisplaySelectionLabels
          >
            <Dropdown.Option value="MSTeams">Microsoft Teams</Dropdown.Option>
            <Dropdown.Option value="Pulsar">Pulsar</Dropdown.Option>
          </Dropdown>
        </FormControl>

        {type === 'MSTeams' && (
          <FormControl isInvalid={!!validationError}>
            <FormControl.Label>Webhook URL</FormControl.Label>
            <Input
              isDisabled={isBusy}
              onChange={(e: ChangeEvent<HTMLInputElement>) => {
                setUrl(e.target.value);
                setValidationError(null);
              }}
              placeholder="https://legogroup.webhook.office.com/webhookb2/..."
              value={url}
            />
            <Text fontSize="sm">Incoming webhook URL from your Microsoft Teams channel.</Text>
          </FormControl>
        )}

        {type === 'Pulsar' && (
          <>
            <FormControl isInvalid={!!validationError}>
              <FormControl.Label>API Name</FormControl.Label>
              <Input
                isDisabled={isBusy}
                onChange={(e: ChangeEvent<HTMLInputElement>) => {
                  setApiName(e.target.value);
                  setValidationError(null);
                }}
                placeholder="emma-device-events"
                value={apiName}
              />
              <Text fontSize="sm">AMMA AsyncAPI name.</Text>
            </FormControl>

            <FormControl isInvalid={!!validationError}>
              <FormControl.Label>AMMA Workspace Name</FormControl.Label>
              <Input
                isDisabled={isBusy}
                onChange={(e: ChangeEvent<HTMLInputElement>) => {
                  setAmmaWorkspaceName(e.target.value);
                  setValidationError(null);
                }}
                placeholder="my-amma-workspace"
                value={ammaWorkspaceName}
              />
            </FormControl>

            <FormControl isInvalid={!!validationError}>
              <FormControl.Label>Topic</FormControl.Label>
              <Input
                isDisabled={isBusy}
                onChange={(e: ChangeEvent<HTMLInputElement>) => {
                  setTopic(e.target.value);
                  setValidationError(null);
                }}
                placeholder="device-events"
                value={topic}
              />
              <Text fontSize="sm">
                Topic name only (not the full <code>persistent://...</code> path).
              </Text>
            </FormControl>
          </>
        )}

        {validationError && (
          <FormControl isInvalid>
            <FormControl.ErrorText>{validationError}</FormControl.ErrorText>
          </FormControl>
        )}

        <SubmitStatusIndicator
          errorMessage={submitError}
          label="Event Connection"
          onReset={() => {
            setSubmitStatus('idle');
            setSubmitError(null);
          }}
          onRetry={() => void handleSubmit()}
          status={submitStatus}
          submittingText="Creating event connection..."
          successText="Event connection created successfully"
        />

        <HorizontalStack justify="flex-end" spacing={3}>
          <Button isDisabled={isBusy} onClick={onCancel} variant="ghost">
            Cancel
          </Button>
          <Button isDisabled={isBusy} isLoading={submitStatus === 'submitting'} type="submit">
            Create
          </Button>
        </HorizontalStack>
      </VerticalStack>
    </form>
  );
};
